import * as api from 'api';

import Dates from '../Dates/Dates';
import Select from '../Select/Select';
import Tools from '../Tools/Tools';
/**
 * Třída pro obsluhu přehledu/statistik.
 */
class Stats {
	constructor() {
		this.section = document.querySelector('.Page-section-createReport');

		if (this.section) {
			this.handleSelects();
			this.handleInputs();
			this.generateReportName();
			this.listenOnCalendarChange();
		}
	}

	/**
	 * Pokud uživatel změní datum pomocí komponenty kalendář,
	 * tak je vygenerováno nové jméno reportu.
	 */
	listenOnCalendarChange() {
		window.addEventListener('calendar-changed', () => {
			this.generateReportName();
		});
	}

	/**
	 * Zajišťuje chování a ovládání selectů pomocí klávesnice
	 * a propisování vybraných hodnot do inputů formuláře.
	 */
	handleSelects() {
		// Zde budou ukládány instance selectů pro případné nastavení podle šablony.
		this.selects = {};

		// Selecty pro nastavení základních údajů a šablony
		const selectsClasses = [
			'Select-template',
			'Select-overview-type',
			'Select-displayed-period',
			'Select-run-period',
			'Select-attachment',
		];

		selectsClasses.forEach((selectClass) => {
			const selectElem = this.section.querySelector('.' + selectClass);

			if (selectElem) {
				const select = new Select();

				select.initSelectWithElement(
					selectElem,
					selectClass === 'Select-template' ? this.onSelectTemplate.bind(this) : this.onSelectBasicData.bind(this),
				);

				this.selects[selectElem.getAttribute('data-name')] = select;
			}
		});

		// Select výběru webů (Vytváření reportu).
		this.websForSelection = Array.from(this.section.querySelectorAll('input[name="item_list"]'));

		const selectWebSelectionElem = this.section.querySelector('.Select-webs-selection');

		const selectWebsSelection = new Select();

		selectWebsSelection.initSelectWithElement(selectWebSelectionElem, this.onSelectWebsSelection.bind(this));

		this.selects[selectWebSelectionElem.getAttribute('data-name')] = selectWebsSelection;
	}

	/**
	 * Pokud uživatel vybere šablonu, tak se její předvolby stáhnou z API
	 * a report se podle ní nastaví.
	 * @param {string} selected
	 * @param {object} select
	 */
	onSelectTemplate(selected, select) {
		const id = Number(selected);

		if (id) {
			api
				// eslint-disable-next-line typescript-eslint/restrict-template-expressions
				.GET(`/app/api/reports/template?templateId=${id}`)
				.then(({ data }) => {
					this.setReportByTemplate(data);
				})
				.catch((error) => {
					console.error(error);
				});
		}

		select.toggleSelect();
		select.setSelectOption(selected);
		select.serviceHiddenInputs(selected);
	}

	/**
	 * Nastaví report podle předvoleb šablony.
	 * @param {object} settings
	 */
	setReportByTemplate(settings) {
		for (const key in settings) {
			const setting = settings[key];

			// Nastavuji Select.
			if (this.selects[key]) {
				// V případě výběrů webu musím nastavit select i jednotlivé checkboxy.
				if (key === 'items_to_include') {
					this.setWebSelection(setting, this.selects[key]);

					this.websForSelection.forEach((item) => {
						if (settings.item_list.indexOf(Number(item.value)) >= 0) {
							item.checked = true;
						} else {
							item.checked = false;
						}
					});
				} else {
					this.selects[key].setSelectOption(setting);
					this.selects[key].serviceHiddenInputs(setting);
				}
			} else if (!Array.isArray(setting)) {
				// Nastavuji input.
				const input = this.section.querySelector(`input[name="${key}"]`);

				if (input) {
					if (typeof setting === 'boolean') {
						input.checked = setting;
					} else {
						input.value = setting;
					}
				}

				this.setCalendarButton(key, setting);
			}
		}
	}

	/**
	 * Nastaví hodnoty v tlačítku kalendáře.
	 * @param {string} key
	 * @param {string} setting
	 */
	setCalendarButton(key, setting) {
		if (key === 'period') {
			this.section.querySelector('#CalendarButton').setAttribute('data-calendar-period-option', setting);
		} else if (key === 'period_end') {
			const date = Dates.timestampToString(Date.parse(setting));

			this.section.querySelector('#CalendarButton span:nth-child(2)').innerText = date;
		} else if (key === 'period_start') {
			const date = Dates.timestampToString(Date.parse(setting));

			this.section.querySelector('#CalendarButton span:nth-child(1)').innerText = date;
		}
	}

	/**
	 * Nastavý list webů disabled property.
	 * @param {string} selected
	 * @param {Object} select
	 */
	setWebSelection(selected, select) {
		select.setSelectOption(selected);
		select.serviceHiddenInputs(selected);

		switch (selected) {
			case 'allItems':
				this.setWebsSelection(true, true);

				break;
			case 'selectedItems':
				this.setWebsSelection(false, false);

				break;
			case 'allItemsIncludingDeleted':
				this.setWebsSelection(true, true, true);

				break;
		}
	}

	/**
	 * Výběr webů - všechny webu | včetně smazaných | pouze vybrané.
	 * @param {string} selected
	 * @param {Object} select
	 */
	onSelectWebsSelection(selected, select) {
		select.toggleSelect();

		if (selected !== select.getSelectedOption()) {
			this.setWebSelection(selected, select);
			this.generateReportName();
		}
	}

	/**
	 * Získá 'Včera' apod. podle nastavení kalendáři.
	 * @param {string} name
	 */
	getTimePeriodTranslation(name) {
		const { value } = this.section.querySelector(`.${name}`);

		const index = window.SSP.translations.calendar.intervals.map((interval) => interval[0]).indexOf(value);

		return window.SSP.translations.calendar.intervals[index][1];
	}

	/**
	 * Vygeneruje jméno reportu podle nastavení.
	 */
	generateReportName() {
		const selectNames = [
			'Select-overview-type',
			'Select-displayed-period',
			'Calendar-input-period',
			'Select-webs-selection',
		];

		let reportName = '';

		selectNames.forEach((name) => {
			if (name === 'Calendar-input-period') {
				reportName += this.getTimePeriodTranslation(name) + ' ';
			} else {
				reportName += this.section.querySelector(`.${name} .Select-option.is-selected`).innerHTML + ' ';
			}
		});

		this.section.getElementsByClassName('Stats-overview-name')[0].value = reportName;
	}

	/**
	 * Označí/odznačí weby.
	 * @param {boolean} checked
	 * @param {boolean} disabled
	 * @param {boolean} includeDeleted Označit i smazané weby.
	 */
	setWebsSelection(checked, disabled, includeDeleted = false) {
		this.websForSelection.forEach((web) => {
			web.checked = false;
			web.disabled = disabled;

			if (web.getAttribute('data-deleted') === 'false') {
				web.checked = checked;
			} else if (includeDeleted) {
				web.checked = checked;
			}
		});
	}

	/**
	 * Zavře select a vygeneruje input s vybranou hodnotou.
	 * @param {string} selected Vybraná option selectu.
	 * @param {object} that this v třídě Select
	 */
	onSelectBasicData(selected, select) {
		select.toggleSelect();

		if (selected !== select.getSelectedOption()) {
			select.setSelectOption(selected);
			select.serviceHiddenInputs(selected);
			this.generateReportName();
		}
	}

	/**
	 * Obsula inputu na stránce.
	 * Pokud je checked příslušný input, tak se zobrazí/schová další sekce,
	 * která obsauje např. select.
	 */
	handleInputs() {
		// Sekce -> Chcete uložit nastavení na jindy?
		const mailInput = this.section.querySelector('input[name="email"]');

		const connectReportWrapper = this.section.querySelector('.Column-connect-like');

		Array.from(this.section.querySelectorAll('input[name="notify"]')).forEach((input) => {
			if (input.checked && input.value === 'notification') {
				mailInput.setAttribute('disabled', 'disabled');
				connectReportWrapper.classList.add('is-invisible');
			}

			input.addEventListener('change', (e) => {
				if (e.target.value === 'email') {
					mailInput.removeAttribute('disabled');
					connectReportWrapper.classList.remove('is-invisible');
				} else {
					const errorElem = mailInput.nextElementSibling.nextElementSibling;

					if (errorElem && errorElem.classList.contains('Error')) {
						Tools.removeElement(errorElem);
						mailInput.classList.remove('is-invalid');
					}

					mailInput.setAttribute('disabled', 'disabled');
					connectReportWrapper.classList.add('is-invisible');
				}
			});
		});

		// Sekce -> Chcete report opakovat?
		const repeatableInput = this.section.querySelector('input[name="repeatable"]');

		const repeatableSelect = this.section.querySelector('.Column-repeatable');

		if (!repeatableInput.checked) {
			repeatableSelect.classList.add('is-invisible');
		}

		repeatableInput.addEventListener('change', (e) => {
			if (e.target.checked) {
				repeatableSelect.classList.remove('is-invisible');
			} else {
				repeatableSelect.classList.add('is-invisible');
			}
		});
	}
}

export default Stats;
